import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { alpha, Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import { StickyNote2Outlined as NoteIcon } from "@mui/icons-material";
import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  format,
  startOfMonth,
} from "date-fns";
import { colors, fonts } from "../../../styles/theme";
import { formatDate } from "../../../utils/formatters";

/** Ein Termin auf dem Zeitstrahl – fachlich schon aufgelöst */
export interface ZeitstrahlPunkt {
  id: string;
  /** ISO-Datum (yyyy-MM-dd) */
  datum: string;
  /** Beschriftung über dem Punkt und erste Zeile im Tooltip, z. B. die Bezeichnung */
  titel: string;
  /** Zweite Zeile im Tooltip, z. B. der Anlass des Termins */
  untertitel?: string;
  /** Notiz zum Termin – im Tooltip mit Notiz-Icon */
  notiz?: string | null;
  /**
   * Wofür der Punkt bei Auswahl steht, wenn mehrere Punkte dasselbe meinen (z. B. alle Termine
   * eines Nachweises) – geht an `onSelect` und wird mit `selectedId` verglichen. Ohne Angabe `id`.
   */
  selectionId?: string;
}

const auswahlId = (punkt: ZeitstrahlPunkt): string =>
  punkt.selectionId ?? punkt.id;

interface ZeitstrahlProps {
  punkte: ZeitstrahlPunkt[];
  /**
   * Rechte Grenze (ISO), z. B. ein geplantes Zieldatum. Termine danach verlängern den Strahl, der
   * Bereich dahinter ist rot – ein Termin dort kommt zu spät. Ohne Angabe endet der Strahl am
   * spätesten Termin.
   */
  ende?: string | null;
  /** Beschriftung der rechten Grenze, z. B. "Genehmigung geplant" */
  endeLabel?: string;
  /** Hervorgehobener Punkt – die Auswahl, die die Seite gerade hält */
  selectedId?: string | null;
  /**
   * Klick auf einen Punkt (seine `selectionId`); bei einer Gruppe der früheste, im Tooltip jeder
   * einzeln
   */
  onSelect?: (id: string) => void;
}

/** Vorlauf der Warnzone ab heute – dieselben 14 Tage wie `getUrgency` */
const WARNTAGE = 14;
/** Punkte, die näher beieinander liegen, werden zu einer Gruppe zusammengefasst */
const MIN_ABSTAND_PX = 22;
/** Luft links und rechts, damit Punkte am Rand nicht abgeschnitten werden */
const RAND_PX = 12;
const PUNKT_PX = 12;
const GRUPPE_PX = 22;
const STRAHL_PX = 4;
/** Höhe der Heute-Kerbe – ragt über den dünnen Strahl hinaus */
const HEUTE_PX = 18;
/** Durchmesser des Zielrings */
const ZIEL_PX = 14;
/** Uhr für Termine in den nächsten 14 Tagen – das Uhr-Icon der Nachweistabelle (`DeadlineCell`) */
const UHR_PX = PUNKT_PX;
/** Strichstärke von Ringen, Zielring und Uhr – überall gleich */
const RING_PX = 2;
/** Fuge zwischen Segment und Marke – wie zwischen den Anteilen im Balken der Freigabe-Kennzahl */
const FUGE_PX = 3;

/** Stiel vom Strahl bis zur untersten Beschriftungszeile */
const STIEL_PX = 16;
/** Höhe einer Beschriftung (Titel und Datum) samt Abstand zur nächsten Zeile darüber */
const ZEILE_PX = 36;
/** Beschriftungen höchstens in zwei Zeilen; was danach noch kollidiert, steht nur im Tooltip */
const MAX_ZEILEN = 2;
const BESCHRIFTUNG_MAX_PX = 120;
const BESCHRIFTUNG_LUFT_PX = 8;
/** Unter dem Strahl: Monate bzw. die zwei Zeilen der Kerben und etwas Luft */
const UNTEN_PX = 70;

type Zone = "ueberfaellig" | "bald" | "normal" | "zuSpaet";

const ZONEN_FARBE: Record<Zone, string> = {
  ueberfaellig: colors.status.error.main,
  bald: colors.status.warning.accent,
  normal: colors.brand.primary,
  zuSpaet: colors.status.error.main,
};

/** Rang für die Farbe einer Gruppe: der dringendste Termin bestimmt sie */
const ZONEN_RANG: Record<Zone, number> = {
  normal: 0,
  bald: 1,
  zuSpaet: 2,
  ueberfaellig: 3,
};

const tag = (iso: string): Date => {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d;
};

const MONATE = [
  "Jan",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
];

interface Gruppe {
  x: number;
  punkte: (ZeitstrahlPunkt & { tag: Date; zone: Zone })[];
  /** Der dringendste Termin bestimmt die Farbe */
  zone: Zone;
  /** Einzelner Termin in den nächsten 14 Tagen: Uhr statt Ring */
  alsUhr: boolean;
  /** Durchmesser der Marke in px */
  groesse: number;
}

/** Beschriftung über einer Gruppe; ohne Zeile passt sie nirgends hin und fehlt */
interface Beschriftung {
  titel: string;
  datum: string;
  links: number;
  breite: number;
  zeile: number | null;
}

/**
 * Horizontaler Zeitstrahl von heute (bzw. dem frühesten überfälligen Termin) bis zu einem
 * Zieldatum, mit Terminen als Punkten.
 *
 * Bewusst zurückhaltend: ein dünner grauer Strahl ohne Warnzone. Die Dringlichkeit tragen die
 * Punkte – normale als Ring, in den nächsten 14 Tagen eine Uhr wie in der Nachweistabelle, ab heute
 * und nach dem Zieldatum rot gefüllt. Ein Termin am heutigen Tag gilt wie in `getUrgency` als
 * überfällig. Eine Gruppe braucht Platz für ihre Anzahl und bleibt deshalb ein gefüllter Kreis.
 *
 * Über jedem Punkt steht an einem Stiel sein Titel und Datum, bei Platzmangel in einer zweiten
 * Zeile; passt auch die nicht, bleibt nur der Tooltip. Punkte, die enger als `MIN_ABSTAND_PX`
 * liegen, fasst der Strahl zu einer Gruppe mit Anzahl zusammen. Der Tooltip nennt jeden Termin
 * einzeln und wählt ihn per Klick.
 *
 * Fachneutral: Was ein Punkt bedeutet, entscheidet der Aufrufer (siehe components/CLAUDE.md).
 */
const Zeitstrahl: React.FC<ZeitstrahlProps> = ({
  punkte,
  ende,
  endeLabel = "Ziel",
  selectedId,
  onSelect,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [breite, setBreite] = useState(0);

  // Die Gruppierung hängt an Pixeln, nicht an Tagen – deshalb die echte Breite.
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const beobachter = new ResizeObserver(([eintrag]) =>
      setBreite(eintrag.contentRect.width),
    );
    beobachter.observe(node);
    return () => beobachter.disconnect();
  }, []);

  const heute = useMemo(() => tag(new Date().toISOString()), []);
  const warnEnde = addDays(heute, WARNTAGE);
  const ziel = ende ? tag(ende) : null;

  const tage = punkte.map((p) => tag(p.datum).getTime());
  const start = new Date(Math.min(heute.getTime(), ...tage));
  const spaetester = Math.max(
    ziel?.getTime() ?? 0,
    warnEnde.getTime(),
    ...tage,
  );
  const schluss = new Date(spaetester);
  const spanne = Math.max(schluss.getTime() - start.getTime(), 1);
  const nutzbar = Math.max(breite - 2 * RAND_PX, 1);
  const xVon = (d: Date) =>
    RAND_PX + ((d.getTime() - start.getTime()) / spanne) * nutzbar;

  const zoneVon = (d: Date): Zone => {
    if (d <= heute) return "ueberfaellig";
    if (ziel && d > ziel) return "zuSpaet";
    if (d <= warnEnde) return "bald";
    return "normal";
  };

  const gruppen = useMemo<Gruppe[]>(() => {
    if (breite === 0) return [];
    const sortiert = punkte
      .map((p) => ({ ...p, tag: tag(p.datum), zone: zoneVon(tag(p.datum)) }))
      .sort((a, b) => a.tag.getTime() - b.tag.getTime());
    const ergebnis: {
      erstesX: number;
      letztesX: number;
      punkte: Gruppe["punkte"];
    }[] = [];
    for (const punkt of sortiert) {
      const x = xVon(punkt.tag);
      const letzte = ergebnis[ergebnis.length - 1];
      // Am ersten Punkt der Gruppe gemessen, nicht am letzten: sonst wüchse eine Kette eng
      // stehender Punkte zu einer Gruppe über den halben Strahl.
      if (letzte && x - letzte.erstesX < MIN_ABSTAND_PX) {
        letzte.punkte.push(punkt);
        letzte.letztesX = x;
      } else {
        ergebnis.push({ erstesX: x, letztesX: x, punkte: [punkt] });
      }
    }
    return ergebnis.map((g) => {
      const zone = g.punkte.reduce<Zone>(
        (dringendste, p) =>
          ZONEN_RANG[p.zone] > ZONEN_RANG[dringendste] ? p.zone : dringendste,
        "normal",
      );
      const istGruppe = g.punkte.length > 1;
      const alsUhr = zone === "bald" && !istGruppe;
      return {
        x: (g.erstesX + g.letztesX) / 2,
        punkte: g.punkte,
        zone,
        alsUhr,
        groesse: istGruppe ? GRUPPE_PX : alsUhr ? UHR_PX : PUNKT_PX,
      };
    });
    // xVon und zoneVon hängen nur an den aufgeführten Werten
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [punkte, breite, start.getTime(), spanne, ziel?.getTime()]);

  /**
   * Beschriftungen von links nach rechts in die unterste freie Zeile. Die Breite ist geschätzt
   * (Inter, caption) – lange Titel kürzt die Darstellung mit Ellipse auf genau diese Breite.
   */
  const beschriftungen = useMemo<Beschriftung[]>(() => {
    const zeilenEnde: number[] = Array(MAX_ZEILEN).fill(-Infinity);
    return gruppen.map((gruppe) => {
      const erster = gruppe.punkte[0];
      const letzter = gruppe.punkte[gruppe.punkte.length - 1];
      const istGruppe = gruppe.punkte.length > 1;
      const titel = istGruppe
        ? `${gruppe.punkte.length} Termine`
        : erster.titel;
      const datum =
        erster.tag.getTime() === letzter.tag.getTime()
          ? format(erster.tag, "dd.MM.")
          : `${format(erster.tag, "dd.MM.")}–${format(letzter.tag, "dd.MM.")}`;
      const b = Math.ceil(
        Math.min(
          BESCHRIFTUNG_MAX_PX,
          Math.max(titel.length * 6.8, datum.length * 6.2),
        ),
      );
      const links = Math.min(Math.max(gruppe.x - b / 2, 0), breite - b);
      const zeile = zeilenEnde.findIndex(
        (ende) => links >= ende + BESCHRIFTUNG_LUFT_PX,
      );
      if (zeile >= 0) zeilenEnde[zeile] = links + b;
      return {
        titel,
        datum,
        links,
        breite: b,
        zeile: zeile >= 0 ? zeile : null,
      };
    });
  }, [gruppen, breite]);

  // Oben nur so viel Platz, wie die Beschriftungen brauchen.
  const zeilen = Math.max(
    0,
    ...beschriftungen.map((b) => (b.zeile === null ? 0 : b.zeile + 1)),
  );
  const MITTE = zeilen === 0 ? GRUPPE_PX / 2 + 4 : STIEL_PX + zeilen * ZEILE_PX;

  // Monatsstriche; bei langen Spannen nur jeder n-te beschriftet, damit nichts überlappt.
  const monate = differenceInCalendarMonths(schluss, start) + 1;
  const schritt = Math.max(
    1,
    Math.ceil(monate / Math.max(1, Math.floor(nutzbar / 70))),
  );
  const striche: { x: number; label: string }[] = [];
  for (
    let m = startOfMonth(addMonths(start, 1)), i = 0;
    m <= schluss;
    m = addMonths(m, 1), i++
  ) {
    if (i % schritt !== 0) continue;
    const label =
      m.getMonth() === 0 || striche.length === 0
        ? `${MONATE[m.getMonth()]} ${String(m.getFullYear()).slice(2)}`
        : MONATE[m.getMonth()];
    striche.push({ x: xVon(m), label });
  }

  /**
   * Der Strahl als Folge einzelner Pillen: Heute, jeder Termin und der Zieltermin teilen ihn, jede
   * Marke steht in einer Lücke mit `FUGE_PX` Luft zu beiden Seiten. Segmente vor heute und nach
   * dem Zieltermin sind rot, alle anderen grau. Ist zwischen zwei Marken kein Platz, entfällt das
   * Segment.
   */
  const grenzen = [
    { x: xVon(start), halb: 0, marke: false },
    { x: xVon(heute), halb: 1, marke: true },
    ...(ziel ? [{ x: xVon(ziel), halb: ZIEL_PX / 2, marke: true }] : []),
    ...gruppen.map((g) => ({ x: g.x, halb: g.groesse / 2, marke: true })),
    { x: xVon(schluss), halb: 0, marke: false },
  ].sort((a, b) => a.x - b.x);
  const xHeute = xVon(heute);
  const xZiel = ziel ? xVon(ziel) : Infinity;
  const segmente = grenzen.slice(1).flatMap((rechts, i) => {
    const linksGrenze = grenzen[i];
    const von =
      linksGrenze.x + (linksGrenze.marke ? linksGrenze.halb + FUGE_PX : 0);
    const bis = rechts.x - (rechts.marke ? rechts.halb + FUGE_PX : 0);
    if (bis - von < STRAHL_PX) return [];
    const mitte = (von + bis) / 2;
    const rot = mitte < xHeute || mitte > xZiel;
    return [{ von, bis, rot }];
  });

  /** Geschätzte Breite einer Kerbenbeschriftung in px – für Randlage und Monatsabstand */
  const beschriftungsBreite = (bedeutung: string) =>
    Math.max(bedeutung.length, 10) * 6.5;

  /**
   * Wie die Beschriftung zur Kerbe steht: mittig darunter, am Rand bündig – sonst liefe sie über
   * den Strahl hinaus.
   */
  const ausrichtung = (x: number, bedeutung: string) => {
    const halbe = beschriftungsBreite(bedeutung) / 2;
    if (x - halbe < 0) return "links" as const;
    if (x + halbe > breite) return "rechts" as const;
    return "mitte" as const;
  };

  /**
   * Kerbe für Heute (senkrechter Strich durch den Strahl) und Ziel (Zielring). Darunter fett, was
   * sie markiert, und darunter das Datum.
   */
  const kerbe = (
    d: Date,
    bedeutung: string,
    art: "heute" | "ziel",
    key: string,
  ) => {
    const x = xVon(d);
    const lage = ausrichtung(x, bedeutung);
    const marke =
      art === "heute" ? (
        <Box
          sx={{
            width: 2,
            height: HEUTE_PX,
            borderRadius: 1,
            backgroundColor: colors.brand.primary,
          }}
        />
      ) : (
        <Box
          sx={{
            width: ZIEL_PX,
            height: ZIEL_PX,
            boxSizing: "border-box",
            borderRadius: "50%",
            border: `${RING_PX}px solid ${colors.brand.primary}`,
            backgroundColor: colors.background.paper,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              backgroundColor: colors.brand.primary,
            }}
          />
        </Box>
      );
    return (
      <Box
        key={key}
        sx={{
          position: "absolute",
          left: x,
          top: MITTE,
          transform: "translate(-50%, -50%)",
        }}
      >
        {marke}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            mt: `${HEUTE_PX / 2 + 8}px`,
            ...(lage === "links"
              ? { left: 0 }
              : lage === "rechts"
                ? { right: 0 }
                : { left: "50%", transform: "translateX(-50%)" }),
            textAlign:
              lage === "links"
                ? "left"
                : lage === "rechts"
                  ? "right"
                  : "center",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              lineHeight: 1.3,
              color: colors.text.primary,
              fontWeight: fonts.weights.medium,
            }}
          >
            {bedeutung}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              lineHeight: 1.3,
              color: colors.text.secondary,
            }}
          >
            {formatDate(d)}
          </Typography>
        </Box>
      </Box>
    );
  };

  /** Monatsbeschriftungen weichen den Beschriftungen der Kerben – sonst lägen beide übereinander */
  const kerbenBereiche = [
    { x: xVon(heute), bedeutung: "Heute" },
    ...(ziel ? [{ x: xVon(ziel), bedeutung: endeLabel }] : []),
  ].map(({ x, bedeutung }) => {
    const b = beschriftungsBreite(bedeutung);
    const lage = ausrichtung(x, bedeutung);
    const links = lage === "links" ? x : lage === "rechts" ? x - b : x - b / 2;
    return { links: links - 16, rechts: links + b + 16 };
  });
  const sichtbareStriche = striche.filter((strich) =>
    kerbenBereiche.every(
      (bereich) => strich.x < bereich.links || strich.x > bereich.rechts,
    ),
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        height: MITTE + UNTEN_PX,
        width: "100%",
        userSelect: "none",
      }}
    >
      {breite > 0 && (
        <>
          {/* Der Strahl als Segmente zwischen den Marken */}
          {segmente.map((segment) => (
            <Box
              key={segment.von}
              sx={{
                position: "absolute",
                left: segment.von,
                width: segment.bis - segment.von,
                top: MITTE - STRAHL_PX / 2,
                height: STRAHL_PX,
                borderRadius: STRAHL_PX / 2,
                backgroundColor: segment.rot
                  ? colors.reifegradSegment.error
                  : colors.zeitstrahl.strahl,
              }}
            />
          ))}

          {/* Monatsstriche */}
          {sichtbareStriche.map((strich) => (
            <Box
              key={strich.x}
              sx={{
                position: "absolute",
                left: strich.x,
                top: MITTE + STRAHL_PX / 2 + 4,
              }}
            >
              <Box
                sx={{
                  width: "1px",
                  height: 4,
                  backgroundColor: colors.border.default,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  top: 8,
                  transform: "translateX(-50%)",
                  color: colors.text.secondary,
                  fontSize: "0.7rem",
                  whiteSpace: "nowrap",
                }}
              >
                {strich.label}
              </Typography>
            </Box>
          ))}

          {kerbe(heute, "Heute", "heute", "heute")}
          {ziel && kerbe(ziel, endeLabel, "ziel", "ziel")}

          {/* Termine */}
          {gruppen.map((gruppe, index) => {
            const erster = gruppe.punkte[0];
            const { zone, alsUhr, groesse } = gruppe;
            const farbe = ZONEN_FARBE[zone];
            const gefuellt = zone !== "normal";
            const istGruppe = gruppe.punkte.length > 1;
            const gewaehlt = gruppe.punkte.some(
              (p) => auswahlId(p) === selectedId,
            );
            const beschriftung = beschriftungen[index];
            const stiel =
              beschriftung?.zeile != null
                ? STIEL_PX + beschriftung.zeile * ZEILE_PX
                : 0;

            return (
              <Tooltip
                key={`${erster.id}-${gruppe.punkte.length}`}
                arrow
                title={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      py: 0.5,
                    }}
                  >
                    {gruppe.punkte.map((p) => (
                      <ButtonBase
                        key={p.id}
                        onClick={() => onSelect?.(auswahlId(p))}
                        sx={{
                          display: "block",
                          textAlign: "left",
                          borderRadius: 0.5,
                          px: 0.5,
                          "&:hover": {
                            textDecoration: onSelect ? "underline" : "none",
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            fontWeight: fonts.weights.semiBold,
                          }}
                        >
                          {formatDate(p.tag)} · {p.titel}
                        </Typography>
                        {p.untertitel && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", opacity: 0.8 }}
                          >
                            {p.untertitel}
                          </Typography>
                        )}
                        {p.notiz && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              alignItems: "flex-start",
                              mt: 0.25,
                            }}
                          >
                            <NoteIcon
                              aria-label="Notiz"
                              sx={{ fontSize: 14, mt: "2px", flexShrink: 0 }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ display: "block", whiteSpace: "pre-line" }}
                            >
                              {p.notiz}
                            </Typography>
                          </Box>
                        )}
                      </ButtonBase>
                    ))}
                  </Box>
                }
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: gruppe.x,
                    top: MITTE,
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                  }}
                >
                  {stiel > 0 && (
                    <>
                      {/* Stiel vom Punkt zur Beschriftung */}
                      <Box
                        sx={{
                          position: "absolute",
                          left: "50%",
                          bottom: "50%",
                          width: "1px",
                          height: stiel,
                          backgroundColor: colors.border.divider,
                        }}
                      />
                      <ButtonBase
                        onClick={() => onSelect?.(auswahlId(erster))}
                        tabIndex={-1}
                        sx={{
                          position: "absolute",
                          bottom: `calc(50% + ${stiel}px)`,
                          left: beschriftung.links - gruppe.x + groesse / 2,
                          width: beschriftung.breite,
                          display: "block",
                          pb: 0.5,
                          textAlign:
                            beschriftung.links <= 0
                              ? "left"
                              : beschriftung.links + beschriftung.breite >=
                                  breite
                                ? "right"
                                : "center",
                          cursor: onSelect ? "pointer" : "default",
                        }}
                      >
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{
                            display: "block",
                            lineHeight: 1.3,
                            color: colors.text.primary,
                            fontWeight: gewaehlt
                              ? fonts.weights.semiBold
                              : fonts.weights.medium,
                          }}
                        >
                          {beschriftung.titel}
                        </Typography>
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{
                            display: "block",
                            lineHeight: 1.3,
                            color: gefuellt ? farbe : colors.text.secondary,
                            fontSize: "0.7rem",
                          }}
                        >
                          {beschriftung.datum}
                        </Typography>
                      </ButtonBase>
                    </>
                  )}
                  <ButtonBase
                    aria-label={
                      istGruppe
                        ? `${gruppe.punkte.length} Termine ab ${formatDate(erster.tag)}`
                        : `${erster.titel}, ${formatDate(erster.tag)}`
                    }
                    onClick={() => onSelect?.(auswahlId(erster))}
                    sx={{
                      width: groesse,
                      height: groesse,
                      boxSizing: "border-box",
                      borderRadius: "50%",
                      border: alsUhr ? "none" : `${RING_PX}px solid ${farbe}`,
                      backgroundColor:
                        gefuellt && !alsUhr ? farbe : colors.background.paper,
                      color: gefuellt ? colors.background.paper : farbe,
                      // Weißer Saum trennt den Punkt vom Strahl, der Halo zeigt die Auswahl.
                      boxShadow: gewaehlt
                        ? `0 0 0 2px ${colors.background.paper}, 0 0 0 5px ${alpha(farbe, 0.35)}`
                        : `0 0 0 2px ${colors.background.paper}`,
                      fontSize: "0.65rem",
                      fontWeight: fonts.weights.semiBold,
                      cursor: onSelect ? "pointer" : "default",
                      transition: "transform 120ms ease",
                      "&:hover": { transform: "scale(1.15)" },
                    }}
                  >
                    {istGruppe ? (
                      gruppe.punkte.length
                    ) : alsUhr ? (
                      // Nachbau von MUI AccessTime, damit der Ring dieselbe Stärke hat wie die
                      // übrigen Punkte – das Icon selbst skaliert seine Strichstärke mit.
                      <Box
                        component="svg"
                        viewBox={`0 0 ${UHR_PX} ${UHR_PX}`}
                        sx={{ width: UHR_PX, height: UHR_PX, display: "block" }}
                        fill="none"
                        stroke={farbe}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle
                          cx={UHR_PX / 2}
                          cy={UHR_PX / 2}
                          r={(UHR_PX - RING_PX) / 2}
                          strokeWidth={RING_PX}
                        />
                        <path
                          d={`M${UHR_PX / 2} ${UHR_PX / 2 - 2.5}V${UHR_PX / 2}l1.75 1`}
                          strokeWidth={1.25}
                        />
                      </Box>
                    ) : null}
                  </ButtonBase>
                </Box>
              </Tooltip>
            );
          })}
        </>
      )}
    </Box>
  );
};

export default Zeitstrahl;
